import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import LoadingOverlay from "./LoadingOverlay";
import ConfirmDialog from "./ConfirmDialog";
import MaterialGroupModal from "./modals/MaterialGroupModal";

const lbl =
  "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";
const inp =
  "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400";
const thCls =
  "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-r last:border-r-0";
const tdCls =
  "px-4 py-2.5 text-xs text-gray-700 border-r last:border-r-0 whitespace-nowrap";

function NewMaterialGroup() {
  const [groupName, setGroupName] = useState("");
  const [groupCode, setGroupCode] = useState("");
  const [groupNameList, setGroupNameList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploadPending, setUploadPending] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);

  useEffect(() => {
    getGroupNameList();
  }, []);

  const getGroupNameList = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        "https://eps.elkomprintama.com/get/api/store/group",
      );
      setGroupNameList(res.data.result);
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to load groups.");
    } finally {
      setIsLoading(false);
    }
  };

  const insertGroupName = async () => {
    setIsLoading(true);
    try {
      const res = await axios.post(
        "https://eps.elkomprintama.com/post/api/store/group",
        {
          mat_group_name: groupName,
          mat_group_code: groupCode,
        },
      );
      if (res.status === 200) {
        toast.success(res.data.msg);
        getGroupNameList();
        setGroupName("");
        setGroupCode("");
      }
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Insert failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!groupName) {
      toast.warning("Please fill in material group name.");
      return;
    }
    setUploadPending(true);
    setShowConfirm(true);
  };

  return (
    <div className="flex flex-col" style={{ maxHeight: "calc(100vh - 80px)" }}>
      {isLoading && <LoadingOverlay message="Loading…" />}

      {/* Header */}
      <div className="w-full rounded-lg bg-cyan-700 mb-4 flex-shrink-0">
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          New Material Group
        </label>
      </div>

      {/* Body card */}
      <div className="flex-1 min-h-0 flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex-shrink-0 px-6 py-5 border-b border-gray-100"
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-3">
              <label className={lbl}>Material Group Name</label>
              <input
                className={inp}
                name="groupname" type="text"
                placeholder="e.g. PAPER"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value.toUpperCase())}
              />
            </div>
            <div>
              <label className={lbl}>Group Code</label>
              <input
                className={inp}
                name="groupcode" type="text"
                placeholder="e.g. PAP"
                value={groupCode}
                onChange={(e) => setGroupCode(e.target.value.toUpperCase())}
              />
            </div>
          </div>
          <button
            type="submit"
            className="mt-4 w-full py-2.5 rounded-xl text-sm font-bold text-white bg-cyan-700 hover:bg-cyan-800 transition"
          >
            Add Group
          </button>
          <p className="text-xs text-gray-400 mt-2">
            Double-click a row to edit it.
          </p>
        </form>

        {/* Table */}
        <div className="flex-1 min-h-0 overflow-auto">
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 bg-gray-700 text-white uppercase">
              <tr>
                <th className={thCls}>Group ID</th>
                <th className={thCls}>Group Name</th>
                <th className={thCls}>Group Code</th>
              </tr>
            </thead>
            <tbody>
              {groupNameList.length === 0 && !isLoading ? (
                <tr>
                  <td
                    colSpan={3}
                    className="py-12 text-center text-gray-400 italic text-sm"
                  >
                    No groups found.
                  </td>
                </tr>
              ) : (
                groupNameList.map((data, i) => (
                  <tr
                    key={data.group_id}
                    onDoubleClick={() => {
                      setShowModal(true);
                      setModalData(data);
                    }}
                    className={`border-b cursor-pointer hover:bg-cyan-50 ${i % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                  >
                    <td className={`${tdCls} text-gray-400`}>
                      {data.group_id}
                    </td>
                    <td className={`${tdCls} font-medium text-gray-800`}>
                      {data.group_name}
                    </td>
                    <td className={tdCls}>{data.group_code}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer count */}
        {groupNameList.length > 0 && (
          <div className="flex-shrink-0 px-5 py-2.5 border-t border-gray-100 bg-gray-50">
            <span className="text-xs text-gray-400">
              {groupNameList.length} group
              {groupNameList.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>

      {showConfirm && uploadPending && (
        <ConfirmDialog
          message="Are you sure you want to insert this material group?"
          onConfirm={() => {
            setShowConfirm(false);
            insertGroupName();
            setUploadPending(false);
          }}
          onCancel={() => {
            setShowConfirm(false);
            setUploadPending(false);
          }}
        />
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <MaterialGroupModal
            dataSet={modalData}
            onClose={() => setShowModal(false)}
            refreshData={getGroupNameList}
          />
        </div>
      )}
    </div>
  );
}

export default NewMaterialGroup;
